import { getKeycloakToken } from './authShared'
import { getE2EMockMode } from '~/helpers/e2eMock'

async function fetchUserSelfGroups({
  token = getKeycloakToken(),
  includeCounts,
} = {}) {
  const config = useRuntimeConfig()
  const params = {}

  if (typeof includeCounts === 'boolean') {
    params.include_counts = includeCounts
  }

  return $fetch(`${config.public.authUrl}/v2/users/self/groups`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    params,
  })
}

export async function getUserAttribute(userId, token = getKeycloakToken()) {
  const config = useRuntimeConfig()
  return $fetch(`${config.public.authUrl}/v2/users/${userId}/attributes`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
}

export async function setUserAttribute(
  userId,
  attribute,
  value,
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const existing = await getUserAttribute(userId, token)
  const merged = { ...existing?.attributes, [attribute]: [value] }
  const result = await $fetch(
    `${config.public.authUrl}/v2/users/${userId}/attributes`,
    {
      method: 'PUT',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: { attributes: merged },
    }
  )
  const { $keycloak } = useNuxtApp()
  if (typeof $keycloak?.updateToken === 'function') {
    await $keycloak.updateToken(-1)
  }
  return result
}

export async function getUserOrganizationName() {
  const { organizations } = await getUserGroups({ includeCounts: false })
  return organizations[0]?.name || null
}

export async function getUsers({
  page = 1,
  limit = 10,
  groupId = null,
  token = getKeycloakToken(),
  clientName = null,
} = {}) {
  const config = useRuntimeConfig()
  const params = {
    page,
    limit,
  }

  if (groupId) {
    params.group_id = groupId
  }

  if (clientName) {
    params.client_name = clientName
  }

  const response = await $fetch(`${config.public.authUrl}/v2/users`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    params: params,
  })
  return response
}

export async function sendDeleteUser(user, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    await $fetch(`${config.public.authUrl}/v2/users/${user}`, {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    return 'User deleted'
  } catch (error) {
    console.error('Error fetching documents:', error)
    return error
  }
}

export async function activateUser(user, token = getKeycloakToken()) {
  const config = useRuntimeConfig()
  await $fetch(`${config.public.authUrl}/v2/users/${user}/activate`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  return 'User activated'
}
export async function deactivateUser(user, token = getKeycloakToken()) {
  const config = useRuntimeConfig()
  await $fetch(`${config.public.authUrl}/v2/users/${user}/deactivate`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  return 'User deactivated'
}
export async function createUser(
  state,
  group_id = null,
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const query = new URLSearchParams({
    activate: String(state.activate),
    verify_email: String(!state.verify),
    send_email_verify: String(state.verify),
  })

  if (group_id) {
    query.set('group_id', group_id)
  }

  const response = await $fetch(
    `${config.public.authUrl}/v2/users?${query.toString()}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: {
        email: state.email,
        first_name: state.first_name,
        last_name: state.last_name,
        password: state.password,
        temporary_password: true,
        username: state.email,
      },
    }
  )
  return response
}

export async function updateUser(userId, userData, token = getKeycloakToken()) {
  const config = useRuntimeConfig()
  const response = await $fetch(`${config.public.authUrl}/v2/users/${userId}`, {
    method: 'PUT',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: {
      first_name: userData.first_name,
      last_name: userData.last_name,
    },
  })
  return response
}

export async function checkAccess(token = getKeycloakToken()) {
  try {
    const response = await fetchUserSelfGroups({ token })
    for (const group of response) {
      if (group.path.toLowerCase().includes('admin')) {
        return true
      }
    }
    return false
  } catch (error) {
    console.error('Error getting the access', error)
    return false
  }
}

export async function getUserGroups({
  usersOnly = false,
  groupTeamsId = null,
  token = getKeycloakToken(),
  includeCounts,
} = {}) {
  try {
    let response = await fetchUserSelfGroups({ token, includeCounts })
    if (usersOnly === true) {
      response = response.filter(
        (group) => !group.path.toLowerCase().includes('admin')
      )
    }

    const organizations = response.filter(
      (group) => (group.path.match(/\//g) || []).length === 2
    )
    let teams = response.filter(
      (group) => (group.path.match(/\//g) || []).length === 3
    )

    if (groupTeamsId) {
      teams = teams.filter((group) => group.parentId === groupTeamsId)
    }
    return { organizations, teams }
  } catch (error) {
    console.error('Error getting user groups', error)
    return { organizations: [], teams: [] }
  }
}

export async function changeUserData(data, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(`${config.public.authUrl}/v2/users/self`, {
      method: 'PUT',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })
    return response
  } catch (error) {
    console.error('Error changing user data', error)
  }
}

export async function addToOrganization(
  user_email,
  group_id,
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const response = await $fetch(
    `${config.public.authUrl}/v2/groups/${group_id}/users/membership?user_email=${user_email}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response
}

export async function removeFromOrganization(
  user_email,
  group_id,
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const response = await $fetch(
    `${config.public.authUrl}/v2/groups/${group_id}/users/membership?user_email=${user_email}`,
    {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response
}

async function getUserInfo(user_id, token = getKeycloakToken()) {
  const config = useRuntimeConfig()
  const response = await $fetch(
    `${config.public.authUrl}/v2/users/${user_id}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response
}

export async function getUserOrganisationsAndTeams(
  user_id,
  users_only = true,
  group_teams_id = null,
  token = getKeycloakToken()
) {
  const userInfo = await getUserInfo(user_id, token)
  let response = userInfo.groups_membership || []
  if (users_only === true) {
    response = response.filter(
      (group) => !group.path.toLowerCase().includes('admin')
    )
  }
  const organizations = response.filter(
    (group) => (group.path.match(/\//g) || []).length === 2
  )
  let teams = response.filter(
    (group) => (group.path.match(/\//g) || []).length === 3
  )
  if (group_teams_id) {
    teams = teams.filter((team) => team.parent_id !== group_teams_id)
  }

  return { organizations, teams }
}

export async function createTeam(
  group_id,
  team_name,
  team_description = '',
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const response = await $fetch(
    `${config.public.authUrl}/v2/groups/${group_id}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: {
        name: team_name,
        description: team_description,
      },
    }
  )
  return response
}

export async function deleteTeam(team_id, token = getKeycloakToken()) {
  const config = useRuntimeConfig()
  const response = await $fetch(
    `${config.public.authUrl}/v2/groups/${team_id}`,
    {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response
}

export async function getLogos(client_name, token = getKeycloakToken()) {
  if (getE2EMockMode() === 'read') {
    return {
      logo_url: null,
      favicon_url: null,
      login_logo_url: null,
    }
  }

  const config = useRuntimeConfig()
  const response = await $fetch(
    `${config.public.authUrl}/v2/groups/logo?client_name=${client_name}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response
}

export async function downloadLogoGoogle(url) {
  if (!url) {
    return null
  }

  const cacheBuster = `?t=${Date.now()}`
  const urlWithCacheBuster = url.includes('?')
    ? `${url}&t=${Date.now()}`
    : `${url}${cacheBuster}`

  const response = await fetch(urlWithCacheBuster, {
    cache: 'no-store',
  })
  return response.blob()
}

export async function updateOrganizationLogo(
  group_id,
  logo,
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const formData = new FormData()
  formData.append('logo', logo)

  const response = await $fetch(
    `${config.public.authUrl}/v2/groups/${group_id}/logo`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  )
  return response
}

export async function updateOrganizationFavicon(
  group_id,
  favicon,
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const formData = new FormData()
  formData.append('favicon', favicon)

  const response = await $fetch(
    `${config.public.authUrl}/v2/groups/${group_id}/favicon`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  )
  return response
}

export async function updateOrganizationLoginLogo(
  group_id,
  login_logo,
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const formData = new FormData()
  formData.append('logo', login_logo)

  const response = await $fetch(
    `${config.public.authUrl}/v2/groups/${group_id}/logo-login`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  )
  return response
}

export async function searchUsers(
  query,
  group_id,
  page = 1,
  limit = 10,
  token = getKeycloakToken()
) {
  const config = useRuntimeConfig()
  const response = await $fetch(
    `${config.public.authUrl}/v2/users/search?group_id=${group_id}&query=${query}&page=${page}&limit=${limit}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response
}

export async function getMaxActiveUsers(org_id, token = getKeycloakToken()) {
  const config = useRuntimeConfig()
  const response = await $fetch(
    `${config.public.authUrl}/v2/organizations/${org_id}/max-active-users`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response
}
